import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createTttSession,
    deleteTttSession,
    moveTttSession,
    MOVE_POINTS,
    THREAT_BONUS,
    WIN_BONUS,
    DRAW_BONUS,
} from './tttGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'tic_tac_toe';
const { UPLOAD_URL } = ARCADE.tic_tac_toe;
const ACCENT = GAME_ACCENT.tic_tac_toe;
const SIZE = 3;
const HINT = 'Arrows / WASD move cursor · Enter / Space place · Esc pause';

export { MOVE_POINTS, THREAT_BONUS, WIN_BONUS, DRAW_BONUS };

/** Kept for unit tests that previously imported local scoring helpers. */
export const moveIncrementalScore = (board, player) => {
    if (player !== 'X') return 0;
    const opponent = 'O';
    const lines = [];
    for (let i = 0; i < SIZE; i++) {
        lines.push(board[i]);
        lines.push([board[0][i], board[1][i], board[2][i]]);
    }
    lines.push([board[0][0], board[1][1], board[2][2]]);
    lines.push([board[0][2], board[1][1], board[2][0]]);
    let threats = 0;
    for (const line of lines) {
        if (line.filter((c) => c === player).length === 2
            && line.filter((c) => c === opponent).length === 0
            && line.filter((c) => c === '').length === 1) {
            threats += 1;
        }
    }
    return MOVE_POINTS + THREAT_BONUS * threats;
};

export const endGameBonus = (winner) => {
    if (winner === 'X') return WIN_BONUS;
    if (winner == null) return DRAW_BONUS;
    return 0;
};

const emptyBoard = () => Array.from({ length: SIZE }, () => Array(SIZE).fill(''));

const TicTacToe = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [board, setBoard] = useState(emptyBoard);
    const [currentPlayer, setCurrentPlayer] = useState('X');
    const [winner, setWinner] = useState(null);
    const [isGameOver, setIsGameOver] = useState(false);
    const [moveHistory, setMoveHistory] = useState([]);
    const [score, setScore] = useState(0);
    const [cursor, setCursor] = useState({ row: 1, col: 1 });
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session, copyToMoveHistory: true });

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.board)) setBoard(data.board.map((r) => r.slice()));
        setCurrentPlayer(data.current_player || 'X');
        setWinner(data.winner ?? null);
        setIsGameOver(Boolean(data.is_game_over));
        setScore(typeof data.score === 'number' ? data.score : 0);
        setMessage(data.message || '');
        const hist = Array.isArray(data.move_history) ? data.move_history
            : (Array.isArray(data.history) ? data.history : []);
        setMoveHistory(hist);
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            winner: data.winner ?? null,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteTttSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createTttSession();
        if (!data?.session_id) throw new Error('TTT GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setCursor({ row: 1, col: 1 });
        recorder.reset({ init_state: { board: emptyBoard() } });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteTttSession(sid);
        play.endGame({ score });
        setBoard(emptyBoard());
        setCurrentPlayer('X');
        setWinner(null);
        setIsGameOver(false);
        setMoveHistory([]);
        setScore(0);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteTttSession(sid);
    }, []);

    const placeAt = useCallback(async (row, col) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveTttSession(sid, { row, col });
            const meta = applyGm(data);
            const last = Array.isArray(data.move_history)
                ? data.move_history[data.move_history.length - 1]
                : null;
            recorder.recordTick({
                player: last?.player,
                row,
                col,
                score: meta.score,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({ score: meta.score, extra: { winner: meta.winner } });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteTttSession(done);
            }
        } catch (err) {
            console.warn('TTT GM move failed', err);
            setMessage(err?.data?.message || 'Invalid move');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isGameOver, play, recorder, setMessage]);

    const onKey = useCallback((e) => {
        const key = e.key;
        if (['ArrowUp', 'w', 'W'].includes(key)) {
            setCursor((c) => ({ ...c, row: Math.max(0, c.row - 1) }));
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(key)) {
            setCursor((c) => ({ ...c, row: Math.min(SIZE - 1, c.row + 1) }));
            e.preventDefault();
        } else if (['ArrowLeft', 'a', 'A'].includes(key)) {
            setCursor((c) => ({ ...c, col: Math.max(0, c.col - 1) }));
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(key)) {
            setCursor((c) => ({ ...c, col: Math.min(SIZE - 1, c.col + 1) }));
            e.preventDefault();
        } else if (key === 'Enter' || key === ' ') {
            placeAt(cursor.row, cursor.col);
            e.preventDefault();
        }
    }, [cursor, placeAt]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [cursor, placeAt],
    });

    const statusChips = useMemo(() => (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {!isGameOver && <Chip label={`Current: ${currentPlayer}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />}
            {winner
                ? <Chip label={`Winner: ${winner}`} size="small" color="success" />
                : isGameOver
                    ? <Chip label="Draw" size="small" color="warning" />
                    : <Chip label="In progress" size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />}
            <Chip label={`Moves: ${moveHistory.length}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
            <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
        </Stack>
    ), [currentPlayer, isGameOver, moveHistory.length, score, winner]);

    return (
        <ArcadeGameShell
            title="Tic-Tac-Toe"
            titleAccent="Arena"
            subtitle="Play manually or upload a Python agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.tic_tac_toe}
            session={session}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    {statusChips}
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, maxWidth: 360, mx: 'auto' }}>
                        {board.map((row, rowIdx) =>
                            row.map((cell, colIdx) => {
                                const focused = play.isActive && cursor.row === rowIdx && cursor.col === colIdx;
                                return (
                                    <Box
                                        key={`${rowIdx}-${colIdx}`}
                                        sx={{
                                            height: 100,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            backgroundColor: focused ? 'rgba(78,205,196,0.2)' : 'rgba(0,0,0,0.4)',
                                            borderRadius: 2,
                                            border: focused ? `2px solid ${ACCENT}` : '2px solid rgba(255,255,255,0.15)',
                                            color: cell === 'X' ? ACCENT : cell === 'O' ? COLOR.danger : 'rgba(255,255,255,0.35)',
                                            fontSize: '2.4rem', fontWeight: 'bold',
                                            cursor: play.phase === 'playing' && !cell && !isGameOver ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (play.phase !== 'playing') return;
                                            setCursor({ row: rowIdx, col: colIdx });
                                            placeAt(rowIdx, colIdx);
                                        }}
                                    >
                                        {cell || ''}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default TicTacToe;
